import React from 'react';
import { Target, Eye, Award, Users } from 'lucide-react';

export const AboutUs = () => {
  return (
    <section
      id="about"
      className="py-24 md:py-32 px-6 md:px-12 bg-navy-light relative overflow-hidden"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-gold/30 rounded-full mb-6">
            <Users size={20} className="text-gold" />
            <span className="text-gold uppercase tracking-widest text-sm font-semibold">About Us</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
            Our <span className="text-gold-gradient">Story</span>
          </h2>
          <div className="section-divider mt-8" />
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Image Side */}
          <div className="relative">
            <div className="relative h-full min-h-[400px] rounded-lg overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1704565522499-321a97525d9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxoYW5kc2hha2UlMjBzaWxob3VldHRlJTIwYnVzaW5lc3N8ZW58MHx8fHwxNzcyNjQwODkyfDA&ixlib=rb-4.1.0&q=85"
                alt="Agatsya International"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-transparent" />
            </div>
            {/* Decorative Border */}
            <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-gold/30 rounded-lg -z-10" />
          </div>

          {/* Text Side */}
          <div className="flex flex-col justify-center">
            <h3 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
              Building Vision with <span className="text-gold-gradient">Global Excellence</span>
            </h3>
            <div className="space-y-4 text-gray-300 leading-relaxed">
              <p>
                <span className="text-gold font-semibold">Agatsya International</span> is a dynamic
                company founded with a vision to connect India's finest agricultural products with global markets
                while providing comprehensive financial services to businesses of all sizes.
              </p>
              <p>
                The name <span className="text-gold-gradient font-semibold">"Agatsya"</span> draws inspiration from
                the revered sage Agastya, symbolizing wisdom, vision, and the bridging of different worlds. Combined
                with <span className="text-silver font-semibold">"International,"</span> it represents our commitment
                to global excellence and cross-border collaboration.
              </p>
              <p>
                As a company, we bring fresh perspectives and innovative approaches to both export trade and
                financial services. Our dual-faceted business model allows us to serve clients holistically, from
                sourcing premium products to managing their financial compliance.
              </p>
              <p>
                We are committed to building lasting relationships with our clients based on quality, transparency,
                and professionalism. Our goal is to become a trusted partner in your global business journey.
              </p>
            </div>
          </div>
        </div>

        {/* Values Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="glass-panel p-8 rounded-lg text-center hover-lift" data-testid="value-quality">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/10 border border-gold/30 mb-4">
              <Award size={32} className="text-gold" />
            </div>
            <h4 className="text-xl font-heading font-semibold text-white mb-3">Quality First</h4>
            <p className="text-gray-300 text-sm">Premium products and services that exceed expectations.</p>
          </div>

          <div className="glass-panel p-8 rounded-lg text-center hover-lift" data-testid="value-integrity">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/10 border border-gold/30 mb-4">
              <Target size={32} className="text-gold" />
            </div>
            <h4 className="text-xl font-heading font-semibold text-white mb-3">Integrity</h4>
            <p className="text-gray-300 text-sm">Transparent and ethical business practices always.</p>
          </div>

          <div className="glass-panel p-8 rounded-lg text-center hover-lift" data-testid="value-innovation">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/10 border border-gold/30 mb-4">
              <Eye size={32} className="text-gold" />
            </div>
            <h4 className="text-xl font-heading font-semibold text-white mb-3">Vision</h4>
            <p className="text-gray-300 text-sm">Forward-thinking solutions for global challenges.</p>
          </div>

          <div className="glass-panel p-8 rounded-lg text-center hover-lift" data-testid="value-trust">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/10 border border-gold/30 mb-4">
              <Users size={32} className="text-gold" />
            </div>
            <h4 className="text-xl font-heading font-semibold text-white mb-3">Partnership</h4>
            <p className="text-gray-300 text-sm">Building lasting relationships with our clients.</p>
          </div>
        </div>
      </div>
    </section>
  );
};
